import styled, { css } from 'styled-components';

/* Phones: one column under the top bar — Full Draft / Your Picks / Team Picks tabs switch it — and
   the pick panel as a bottom sheet. DraftBoard reads the same query with useMediaQuery for the
   sheet's behaviour, so the two can't disagree. */
export const MOBILE_QUERY = '(max-width: 768px)';
const MOBILE = `@media only screen and ${MOBILE_QUERY}`;

/* Mobile column height: the screen below the site banner and the top bar, less the collapsed
   sheet's header (--sheet-peek, measured by DraftBoard) so nothing ends up under it. dvh where
   supported, so the column follows a phone browser's collapsing address bar. */
const mobileColumnHeight = css`
  height: calc(100vh - var(--banner-h, 90px) - ${({ $top }) => $top}px - var(--sheet-peek, 0px));
  height: calc(100dvh - var(--banner-h, 90px) - ${({ $top }) => $top}px - var(--sheet-peek, 0px));
`;

/* ── Fixed top bar ─────────────────────────────────────────────────── */

export const TopBar = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: rgb(229 229 229);
  display: flex;
  flex-direction: column;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
`;

/* One row: Start / Pause and Cancel on the left. The centre and right tracks
   of the 1fr/auto/1fr grid are empty. */
export const TopBarRow = styled.div`
  width: 100%;
  max-width: 1616px;
  padding: 12px 18px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 16px;

  /* Large desktops / 4K: matches ContentRow so the bar stays aligned with the
     columns below it. 100vw-based so the page always keeps a margin. */
  @media only screen and (min-width: 1700px) {
    max-width: min(1900px, calc(100vw - 80px));
  }

  @media only screen and (min-width: 2100px) {
    max-width: min(2300px, calc(100vw - 120px));
  }

  /* 4K and up: track the viewport at 90vw rather than a fixed cap. The lower
     bound keeps the 3000px tier from being a step DOWN from 2800px, and the
     upper one stops an ultrawide stretching the columns indefinitely. */
  @media only screen and (min-width: 3000px) {
    max-width: clamp(2800px, 90vw, 4000px);
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    justify-items: center;
    row-gap: 12px;
  }

  ${MOBILE} {
    padding: 10px 16px;
  }
`;

/* Mobile only: picks what the column under the top bar shows. Same look as the pick panel's
   Draft a Player / Trade tabs. */
export const MobileViewTabs = styled.div`
  display: flex;
  width: 100%;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
`;

export const MobileViewTab = styled.button`
  flex: 1;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: 16px;
  font-weight: 700;
  background: none;
  border: none;
  padding: 11px 4px 9px;
  margin-bottom: -1px;
  cursor: pointer;
  white-space: nowrap;
  color: ${({ $active }) => ($active ? '#012337' : '#50647b')};
  border-bottom: 3px solid ${({ $active }) => ($active ? '#004ea3' : 'transparent')};
`;

export const TeamInfoLeft = styled.div`
  justify-self: start;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  @media (max-width: 900px) {
    justify-self: center;
  }

  /* Phones: Start/Pause on the left edge, Cancel on the right. */
  ${MOBILE} {
    width: 100%;
    justify-content: space-between;
  }
`;

export const TeamInfoLogo = styled.img`
  width: clamp(80px, 5vw, 192px);
  height: clamp(80px, 5vw, 192px);
  object-fit: contain;
  flex-shrink: 0;
`;

export const TeamInfoName = styled.div`
  font-family: ui-sans-serif, system-ui, -apple-system, sans-serif;
  font-weight: 800;
  font-size: clamp(26px, 1.62vw, 62px);
  color: #012337;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const NextPickInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  min-width: 0;
`;

export const NextPickLabel = styled.div`
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

export const NextPickValue = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(8px, 0.5vw, 19px);
  font-size: clamp(20px, 1.25vw, 48px);
  font-weight: 700;
  color: #012337;
  line-height: 1.2;
  white-space: nowrap;

  /* Team crest next to the value — says whose pick it is at a glance, which matters once more
     than one team is selected. */
  img {
    width: clamp(26px, 1.6vw, 61px);
    height: clamp(26px, 1.6vw, 61px);
    object-fit: contain;
    flex-shrink: 0;
  }
`;

export const ActionRight = styled.div`
  justify-self: end;
  display: flex;
  align-items: center;
  gap: clamp(14px, 0.88vw, 34px);

  @media (max-width: 900px) {
    justify-self: center;
  }
`;

export const StartDraftBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(9px, 0.56vw, 22px);
  padding: 0 clamp(26px, 1.63vw, 62px);
  height: clamp(40px, 2.5vw, 96px);
  background-color: ${({ $isStart }) => ($isStart ? '#16a34a' : '#012337')};
  border: none;
  border-radius: 100px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(15px, 0.94vw, 36px);
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) { background-color: ${({ $isStart }) => ($isStart ? '#15803d' : '#03111b')}; }
  &:active:not(:disabled) { background-color: ${({ $isStart }) => ($isStart ? '#166534' : '#000')}; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }

  svg {
    width: clamp(12px, 0.75vw, 29px);
    height: clamp(14px, 0.88vw, 34px);
  }
`;

export const OutlineActionBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 clamp(24px, 1.5vw, 58px);
  height: clamp(40px, 2.5vw, 96px);
  background: #fff;
  border: 1px solid #012337;
  border-radius: 100px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 600;
  color: #012337;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: #d3ebfc; }
  &:active { background: #a9d9fa; }
  &:disabled { opacity: 0.45; cursor: not-allowed; }
`;

export const CancelDraftBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 clamp(24px, 1.5vw, 58px);
  height: clamp(40px, 2.5vw, 96px);
  background: transparent;
  border: 1px solid #c6cdd3;
  border-radius: 100px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 600;
  color: #012337;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: #d9dee3; border-color: #7b8b99; }
  &:active { background: #c8cfd6; border-color: #7b8b99; }
`;

/* ── Board loader ───────────────────────────────────────────────────── */

export const BoardLoaderWrap = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 28, 46, 0.92);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(14px, 0.88vw, 34px);
  z-index: 999;
`;

export const BoardLoaderIcon = styled.div`
  font-size: clamp(48px, 3.0vw, 115px);
  line-height: 1;
  animation: bobble 1.4s ease-in-out infinite;

  @keyframes bobble {
    0%, 100% { transform: translateY(0); }
    50%       { transform: translateY(-8px); }
  }
`;

export const BoardLoaderTitle = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(20px, 1.25vw, 48px);
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.3px;
`;

export const BoardLoaderSub = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(15px, 0.94vw, 36px);
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
`;

/* ── Draft board ────────────────────────────────────────────────────── */

export const BoardWrap = styled.div`
  position: relative;
  margin: 0 auto;
`;

/* The column's fixed title. Same bar, padding and type the round banner used when it sat on top,
   so the column still lines up with the other two panels' headers. */
export const BoardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 7px 16px;
  background: linear-gradient(90deg, #012337, #004e7b);
  flex-shrink: 0;

  /* The Full Draft tab above already says it. */
  ${MOBILE} {
    display: none;
  }
`;

export const BoardHeaderText = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 800;
  font-size: clamp(20px, 1.25vw, 48px);
  color: #fff;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

/* Only the list scrolls; the header stays. */
export const BoardScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

/* A section bar inside the list. Sticky at the top of the scroller (just under the column
   header), so the round being viewed always shows; each one sits in its round's wrapper, so the
   next round's bar pushes it out. */
export const RoundBanner = styled.div`
  display: flex;
  align-items: center;
  padding: clamp(8px, 0.5vw, 19px) 16px;
  background: rgb(226, 226, 226);
  border-bottom: 1px solid #cecfcf;
  position: sticky;
  top: 0;
  z-index: 10;
`;

export const RoundBannerText = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 800;
  font-size: clamp(17px, 1.06vw, 41px);
  color: #022142;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

export const PickRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px 0 16px;
  height: clamp(56px, 3.5vw, 134px);
  border-bottom: 1px solid #cecfcf;
  background: ${({ $isCurrent, $selected }) =>
    $isCurrent ? '#eaf4fb' : $selected ? '#dbeafe' : '#f4f4f4'};
  min-width: 325px;
  gap: 8px;
  box-shadow: ${({ $isCurrent }) =>
    $isCurrent ? 'inset 3px 0 0 #2f8fd4' : 'none'};
  @media (min-width: 360px) { min-width: 360px; }

  ${MOBILE} {
    min-width: 0;
    padding: 0 16px 0 12px;
  }
`;

export const PickLeft = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;

  /* Mobile: as wide as the number, logo and team name need, and PickRight takes the rest for the
     player's name. Shrinks only on a row too narrow for both, e.g. On the Clock at 320px. */
  ${MOBILE} {
    flex: 0 1 auto;
  }
`;

export const PickNumCol = styled.div`
  min-width: clamp(24px, 1.5vw, 58px);
  height: clamp(28px, 1.75vw, 67px);
  padding-right: 4px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

export const PickNumText = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(20px, 1.25vw, 48px);
  font-weight: 700;
  color: #000;
  line-height: 1.5;
`;

export const PickLogoWrap = styled.div`
  width: clamp(30px, 1.9vw, 72px);
  height: clamp(30px, 1.9vw, 72px);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 5px;
  border: 0.5px solid #cecfcf;
  border-radius: 9999px;
  background: #fff;
`;

export const PickLogo = styled.img`
  width: clamp(22px, 1.4vw, 53px);
  max-width: 100%;
`;

export const PickInner = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 8px;
`;

export const PickNameCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
`;

export const PickTeamName = styled.div`
  color: #000;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(15px, 0.94vw, 36px);
  font-weight: 600;
  line-height: 1.25;
`;

export const PickRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  gap: 4px;

  /* Mobile: all the space the team side leaves. The minimum is the position badge (44px plus its
     8px margin) and the 4px gap, so the badge never spills out of the row. */
  ${MOBILE} {
    flex: 1 1 0;
    min-width: 56px;
  }
`;

export const OnTheClockBadge = styled.span`
  margin-left: 10px;
  font-size: clamp(11px, 0.69vw, 26px);
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #2f8fd4;
  background: #fff;
  border: 1px solid #2f8fd4;
  border-radius: 999px;
  padding: 3px 9px;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const PickSlotA = styled.div`
  font-size: clamp(15px, 0.94vw, 36px);
  font-weight: 600;
  color: #000;
  text-align: right;
  min-width: 0;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (min-width: 786px) { max-width: clamp(220px, 14vw, 530px); }

  /* Mobile: no fixed cap. The 120px one cut "Drew Mestemaker" short on a row with room to spare;
     PickRight's width is the limit now, and the ellipsis only shows when the name won't fit. */
  ${MOBILE} {
    max-width: none;
  }
`;

export const PickSlotB = styled.div`
  font-size: clamp(13px, 0.81vw, 31px);
  font-weight: 700;
  color: #000;
  margin-left: 8px;
  width: clamp(44px, 2.75vw, 106px);
  flex-shrink: 0;
  text-align: center;
  padding: 2px 6px;
  border-radius: 100px;
  overflow: hidden;
  background: ${({ $color }) => $color || 'transparent'};
`;

/* ── Page shell + split layout ──────────────────────────────────────── */

export const PageShell = styled.div``;

export const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  max-width: 1616px;
  margin-left: auto;
  margin-right: auto;
  gap: 16px;
  padding: 0 10px;

  /* Large desktops / 4K: the three columns sat inside 1616px, leaving most of
     the screen empty. 100vw-based so the page always keeps a margin. */
  @media only screen and (min-width: 1700px) {
    max-width: min(1900px, calc(100vw - 80px));
  }

  @media only screen and (min-width: 2100px) {
    max-width: min(2300px, calc(100vw - 120px));
  }

  /* 4K and up: track the viewport at 90vw rather than a fixed cap. The lower
     bound keeps the 3000px tier from being a step DOWN from 2800px, and the
     upper one stops an ultrawide stretching the columns indefinitely. */
  @media only screen and (min-width: 3000px) {
    max-width: clamp(2800px, 90vw, 4000px);
  }

  ${MOBILE} {
    padding: 0;
    gap: 0;
  }
`;

export const BoardColumn = styled.div`
  min-width: 360px;

  @media only screen and (min-width: 1700px) {
    width: clamp(360px, 21vw, 800px);
    flex-shrink: 0;
  }
  position: sticky;
  top: ${({ $top }) => $top}px;
  height: calc(100vh - var(--banner-h, 90px) - 10px - ${({ $top }) => $top}px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #c6cdd3;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(14, 34, 51, .06);

  ${MOBILE} {
    display: ${({ $mobileHidden }) => ($mobileHidden ? 'none' : 'flex')};
    width: 100%;
    min-width: 0;
    ${mobileColumnHeight};
    border: none;
    border-radius: 0;
    box-shadow: none;
  }
`;

export const PickPanelWrap = styled.div`
  flex: 1;
  min-width: 600px;
  flex-shrink: 0;
  position: sticky;
  height: calc(100vh - var(--banner-h, 90px) - 10px - ${({ $top }) => $top}px);
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  border: 1px solid #c6cdd3;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(14, 34, 51, .06);

  /* Bottom sheet. Its height runs from under the top bar to the bottom of the screen; closed, it
     slides down until only its header (--sheet-peek: clock banner, team and needs, tabs) shows.
     useSheetSwipe drags it with the finger by setting transform inline, then hands back to this.
     Under the modals (ModalShell is 200), over the top bar (100). */
  ${MOBILE} {
    position: fixed;
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    min-width: 0;
    height: calc(100vh - var(--banner-h, 90px) - ${({ $top }) => $top}px);
    height: calc(100dvh - var(--banner-h, 90px) - ${({ $top }) => $top}px);
    z-index: 150;
    border: none;
    border-radius: 14px 14px 0 0;
    box-shadow: 0 -6px 24px rgba(14, 34, 51, 0.18);
    transform: ${({ $sheetOpen }) =>
      $sheetOpen ? 'translateY(0)' : 'translateY(calc(100% - var(--sheet-peek, 0px)))'};
    transition: transform 0.28s ease;
  }
`;

export const PanelLockOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  padding: 24px;
  background: repeating-linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.55) 0px,
    rgba(255, 255, 255, 0.55) 14px,
    rgba(238, 242, 245, 0.55) 14px,
    rgba(238, 242, 245, 0.55) 28px
  );
  backdrop-filter: blur(1px);
  z-index: 20;
`;

export const PanelLockBadge = styled.div`
  width: clamp(76px, 4.75vw, 182px);
  height: clamp(76px, 4.75vw, 182px);
  border-radius: 50%;
  background: #fff;
  border: 1px solid #e3e8ed;
  box-shadow: 0 4px 14px rgba(14, 34, 51, 0.10);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 14px;

  svg {
    width: clamp(32px, 2vw, 76px);
    height: clamp(32px, 2vw, 76px);
    stroke: #0e2233;
    stroke-width: 2;
    fill: none;
  }
`;

export const PanelLockText = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 800;
  font-size: clamp(1.2rem, 1.2vw, 3rem);
  color: #0e2233;
`;

export const PanelLockCTA = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(10px, 0.63vw, 24px);
  margin-top: clamp(20px, 1.25vw, 48px);
  padding: 0 clamp(28px, 1.75vw, 67px);
  height: clamp(48px, 3vw, 115px);
  background: #16a34a;
  border: none;
  border-radius: 100px;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(16px, 1.0vw, 38px);
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(22, 163, 74, 0.35);
  transition: background 0.15s, box-shadow 0.15s;

  &:hover { background: #15803d; box-shadow: 0 6px 18px rgba(22, 163, 74, 0.4); }
  &:active { background: #166534; }

  svg {
    width: clamp(12px, 0.75vw, 29px);
    height: clamp(14px, 0.88vw, 34px);
  }
`;

export const SimPanel = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 50;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.12);
  max-height: 60vh;
  overflow-y: auto;
`;

/* ── Team picks side panel ──────────────────────────────────────────── */

export const TeamPicksPanelWrap = styled.aside`
  width: 380px;

  @media only screen and (min-width: 1700px) {
    width: clamp(380px, 22vw, 900px);
  }
  flex-shrink: 0;
  position: sticky;
  top: ${({ $top }) => $top}px;
  height: calc(100vh - var(--banner-h, 90px) - 10px - ${({ $top }) => $top}px);
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #c6cdd3;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(14, 34, 51, .06);

  @media (max-width: 1100px) {
    position: relative;
    top: 0;
    width: 100%;
    height: auto;
    max-height: 480px;
  }

  ${MOBILE} {
    display: ${({ $mobileHidden }) => ($mobileHidden ? 'none' : 'flex')};
    ${mobileColumnHeight};
    max-height: none;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }
`;

export const TeamPicksPanelHeader = styled.div`
  display: flex;
  align-items: center;
  padding: clamp(7px, 0.44vw, 17px) clamp(16px, 1vw, 38px) 0;
  background: linear-gradient(90deg, #012337, #004e7b);
  flex-shrink: 0;
  gap: clamp(20px, 1.25vw, 48px);
  justify-content: center;

  /* Replaced by the Your Picks / Team Picks tabs under the top bar. */
  ${MOBILE} {
    display: none;
  }
`;

export const TeamPicksPanelTitle = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 800;
  font-size: clamp(20px, 1.25vw, 48px);
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: #fff;
`;

/* Tabs in the panel header. The inactive one stays legible against the navy rather than
   dimming to near-invisible, since it is the only way back to the other view. */
export const TeamPicksTab = styled.button`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 800;
  font-size: clamp(20px, 1.25vw, 48px);
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background: none;
  border: none;
  padding: clamp(2px, 0.13vw, 5px) clamp(16px, 1vw, 38px);
  cursor: pointer;
  white-space: nowrap;
  color: ${({ $active }) => ($active ? '#fff' : '#8fb6cf')};
  border-bottom: clamp(3px, 0.19vw, 7px) solid ${({ $active }) => ($active ? '#03da42' : 'transparent')};

  &:hover { color: #fff; }
`;

export const TeamPicksTabDivider = styled.span`
  width: 1px;
  align-self: stretch;
  margin: 4px 14px;
  background: rgba(255, 255, 255, 0.35);
  flex-shrink: 0;
`;

export const TeamPicksPanelTeam = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px 8px;
  flex-shrink: 0;
`;

/* Single fixed team — logo and name, no dropdown, when there is nothing to switch between. */
export const TeamPicksTeamStatic = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(10px, 0.63vw, 24px);
  min-width: 0;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-weight: 800;
  font-size: clamp(15px, 0.94vw, 36px);
  color: #012337;

  img {
    width: clamp(28px, 1.75vw, 67px);
    height: clamp(28px, 1.75vw, 67px);
    object-fit: contain;
    flex-shrink: 0;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

/* One row of the team dropdown — used for the closed value and each menu item */
export const TeamPicksTeamOption = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  img {
    width: clamp(24px, 1.5vw, 58px);
    height: clamp(24px, 1.5vw, 58px);
    object-fit: contain;
    flex-shrink: 0;
  }

  span {
    font-family: 'Saira Semi Condensed', sans-serif;
    font-size: clamp(15px, 0.94vw, 36px);
    font-weight: 700;
    color: #012337;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const TeamPicksPanelBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

/* The header and every row lay out on the same grid; the track list comes in
   as $template, built from the column config in TeamPicksPanel.jsx, so adding
   or reordering a column is a one-line change there. */
const teamPicksGrid = css`
  display: grid;
  grid-template-columns: ${({ $template }) => $template};
  align-items: center;
  column-gap: clamp(10px, 0.6vw, 23px);
  padding: 0 clamp(16px, 1vw, 38px);
`;

export const TeamPicksTableHeader = styled.div`
  ${teamPicksGrid};
  padding-top: clamp(11px, 0.7vw, 26px);
  padding-bottom: clamp(11px, 0.7vw, 26px);
  background: #e2e2e2;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  font-weight: 800;
  color: #000;
  position: sticky;
  top: 0;
  z-index: 2;
`;

export const TeamPicksRow = styled.div`
  ${teamPicksGrid};
  height: clamp(56px, 3.5vw, 134px);
  border-bottom: 1px solid rgb(229, 231, 235);
`;

export const TeamPicksCell = styled.div`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: ${({ $align }) => $align || 'left'};
`;

/* Pick number over round/iteration, stacked the same way the player cell stacks name over
   school — bold navy lead, smaller muted line beneath. */
export const TeamPicksPickCol = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const TeamPicksRoundNum = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(12px, 0.75vw, 29px);
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
`;

export const TeamPicksPickNum = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(16px, 1.0vw, 38px);
  font-weight: 900;
  color: #012337;
  line-height: 1.25;
`;

export const TeamPicksPlayerCol = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

export const TeamPicksPlayerName = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(15px, 0.94vw, 36px);
  font-weight: 600;
  color: #000;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const TeamPicksPlayerSchool = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(13px, 0.81vw, 31px);
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TeamPicksPositionBadge = styled.div`
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(13px, 0.81vw, 31px);
  font-weight: 700;
  color: #000;
  background: ${({ $color }) => $color || 'transparent'};
  border-radius: 100px;
  padding: 2px 0;
  text-align: center;
`;

export const TeamPicksRank = styled.div`
  font-size: clamp(15px, 0.94vw, 36px);
  font-weight: 600;
  color: #000;
  text-align: center;
`;

export const TeamPicksEmpty = styled.div`
  padding: 24px 16px;
  text-align: center;
  font-family: 'Saira Semi Condensed', sans-serif;
  font-size: clamp(14px, 0.88vw, 34px);
  color: #6b7280;
`;
